import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ShoppingBag, Loader2, Minus, Plus } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import Layout from "@/components/layout/Layout";
import AnimateOnScroll from "@/components/AnimateOnScroll";
import { handleApiError } from "@/lib/errors";
import { emitFlyToCart } from "@/lib/fly-to-cart";
import {
  getProductBySlug,
  formatPriceNok,
  type Product,
  type ProductCategory,
} from "@/lib/shop";
import { Seo } from "@/components/Seo";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  ProductTrustPanel,
  PaymentBadges,
} from "@/components/shop/ProductTrustPanel";
import { ImpactBanner } from "@/components/shop/ImpactBanner";
import { StockBadge, stockStatusOf } from "@/components/shop/StockBadge";

const CATEGORY_LABEL: Record<ProductCategory, string> = {
  mennesker: "Til deg",
  hunder: "Til hunden din",
};

const SITE = "https://www.haap-naturlab.no";

function breadcrumbJsonLd(product: Product) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Hjem", item: `${SITE}/` },
      {
        "@type": "ListItem",
        position: 2,
        name: "Nettbutikk",
        item: `${SITE}/nettbutikk`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: CATEGORY_LABEL[product.category],
        item: `${SITE}/nettbutikk?kategori=${product.category}`,
      },
      {
        "@type": "ListItem",
        position: 4,
        name: product.title,
        item: `${SITE}/produkt/${product.slug}`,
      },
    ],
  };
}

function productJsonLd(product: Product) {
  const variant = product.variants[0];
  const imageUrls = product.images.map((i) => i.url);
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.title,
    description: product.description ?? undefined,
    image: imageUrls.length === 1 ? imageUrls[0] : imageUrls,
    sku: variant?.sku ?? undefined,
    brand: { "@type": "Brand", name: "Håp Naturlab" },
    offers: variant
      ? {
          "@type": "Offer",
          url: `https://www.haap-naturlab.no/produkt/${product.slug}`,
          priceCurrency: "NOK",
          price: (variant.priceOre / 100).toFixed(2),
          availability: "https://schema.org/InStock",
          itemCondition: "https://schema.org/NewCondition",
          shippingDetails: {
            "@type": "OfferShippingDetails",
            shippingRate: {
              "@type": "MonetaryAmount",
              value: "99.00",
              currency: "NOK",
            },
            shippingDestination: {
              "@type": "DefinedRegion",
              addressCountry: "NO",
            },
            deliveryTime: {
              "@type": "ShippingDeliveryTime",
              handlingTime: {
                "@type": "QuantitativeValue",
                minValue: 1,
                maxValue: 2,
                unitCode: "DAY",
              },
              transitTime: {
                "@type": "QuantitativeValue",
                minValue: 2,
                maxValue: 5,
                unitCode: "DAY",
              },
            },
          },
          hasMerchantReturnPolicy: {
            "@type": "MerchantReturnPolicy",
            applicableCountry: "NO",
            returnPolicyCategory:
              "https://schema.org/MerchantReturnFiniteReturnWindow",
            merchantReturnDays: 14,
            returnMethod: "https://schema.org/ReturnByMail",
            returnFees: "https://schema.org/FreeReturn",
          },
        }
      : undefined,
  };
}

interface CollapsibleDescriptionProps {
  html: string | null;
  plain: string | null;
}

const COLLAPSED_HEIGHT = "12rem"; // ~192px

const CollapsibleDescription = ({ html, plain }: CollapsibleDescriptionProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  // Measure once content is rendered. Re-measure on resize because the
  // collapsed height is fixed and a wider viewport may eliminate overflow.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => {
      const max = parseFloat(getComputedStyle(el).fontSize) * 12; // 12rem
      setOverflowing(el.scrollHeight > max + 4);
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [html, plain]);

  if (!html && !plain) return null;

  return (
    <div className="mt-6">
      <div
        ref={ref}
        className="product-description relative overflow-hidden font-body text-foreground/90"
        style={!expanded ? { maxHeight: COLLAPSED_HEIGHT } : undefined}
      >
        {html ? (
          <div dangerouslySetInnerHTML={{ __html: html }} />
        ) : (
          <div className="space-y-3">
            {plain!
              .split(/\n\s*\n/)
              .map((para, i) => (
                <p key={i} className="whitespace-pre-line">
                  {para}
                </p>
              ))}
          </div>
        )}
        {!expanded && overflowing && (
          <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-background to-transparent" />
        )}
      </div>
      {overflowing && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 text-sm font-semibold text-primary underline-offset-2 hover:underline"
        >
          {expanded ? "Vis mindre" : "Les mer"}
        </button>
      )}
    </div>
  );
};

const ProductDetail = () => {
  const { handle } = useParams<{ handle: string }>();
  const [selectedVariantIdx, setSelectedVariantIdx] = useState(0);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const addToCartRef = useRef<HTMLButtonElement>(null);
  const addItem = useCartStore((s) => s.addItem);

  // Reset quantity when variant changes so stock caps apply cleanly.
  useEffect(() => {
    setQuantity(1);
  }, [selectedVariantIdx]);

  const {
    data: product = null,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ["shop", "product", handle],
    queryFn: () => getProductBySlug(handle!),
    enabled: Boolean(handle),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (error) {
      handleApiError(error, {
        source: "ProductDetail.getProductBySlug",
        fallbackMessage: "Kunne ikke laste produktet. Prøv igjen senere.",
        context: { slug: handle },
      });
    }
  }, [error, handle]);

  useEffect(() => {
    if (handle) window.scrollTo(0, 0);
  }, [handle]);

  if (loading) {
    return (
      <Layout>
        <div className="flex min-h-[60vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout>
        <Seo
          title="Produktet ble ikke funnet – Håp Naturlab"
          description="Dette produktet eksisterer ikke lenger."
          noIndex
        />
        <div className="flex min-h-[60vh] items-center justify-center text-center">
          <div>
            <h1 className="text-2xl font-bold">Produktet ble ikke funnet</h1>
            <Link
              to="/nettbutikk"
              className="mt-4 inline-flex items-center gap-2 text-primary font-body hover:underline"
            >
              <ArrowLeft className="h-4 w-4" /> Tilbake til butikken
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const variant = product.variants[selectedVariantIdx];
  const images = product.images;
  const primaryImage = images[0]?.url;
  const stock = stockStatusOf(variant);
  const soldOut = stock === "out_of_stock";
  // Cap max quantity at current inventory when known, else allow up to 10
  // (soft cap for the + button, actual cart has no hard limit).
  const maxQty = variant?.inventory ?? 10;

  const handleAddToCart = () => {
    if (!variant || soldOut) return;
    addItem(product, variant, quantity);
    const button = addToCartRef.current;
    if (button && primaryImage) {
      emitFlyToCart({
        sourceRect: button.getBoundingClientRect(),
        imageUrl: primaryImage,
      });
    }
  };

  return (
    <Layout>
      <Seo
        title={`${product.title} – Håp Naturlab`}
        description={
          product.description?.slice(0, 160) ??
          "Naturlig, håndlaget hudpleie der hele overskuddet går til syke barn."
        }
        path={`produkt/${product.slug}`}
        image={primaryImage}
        ogType="product"
        jsonLd={[productJsonLd(product), breadcrumbJsonLd(product)]}
      />
      <div className="mx-auto max-w-6xl px-6 py-12">
        <Breadcrumb className="mb-8">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Hjem</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/nettbutikk">Nettbutikk</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={`/nettbutikk?kategori=${product.category}`}>
                  {CATEGORY_LABEL[product.category]}
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{product.title}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="mb-10">
          <ImpactBanner />
        </div>

        <div className="grid gap-12 md:grid-cols-2">
          <AnimateOnScroll animation="animate-slide-in-left">
            <div>
              <div className="aspect-square overflow-hidden rounded-2xl bg-secondary/30">
                {images[selectedImage] ? (
                  <img
                    src={images[selectedImage].url}
                    alt={images[selectedImage].alt ?? product.title}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-muted-foreground">
                    <ShoppingBag className="h-16 w-16" />
                  </div>
                )}
              </div>
              {images.length > 1 && (
                <div className="mt-4 flex gap-3">
                  {images.map((img, i) => (
                    <button
                      key={img.id}
                      type="button"
                      onClick={() => setSelectedImage(i)}
                      aria-label={`Vis bilde ${i + 1} av ${images.length}`}
                      aria-pressed={i === selectedImage}
                      className={`h-16 w-16 overflow-hidden rounded-lg border-2 transition-all ${
                        i === selectedImage
                          ? "border-primary"
                          : "border-transparent opacity-60 hover:opacity-100"
                      }`}
                    >
                      <img
                        src={img.url}
                        alt=""
                        aria-hidden="true"
                        className="h-full w-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="animate-slide-in-right">
            <div>
              <div className="flex items-start justify-between gap-4">
                <h1 className="font-heading text-3xl font-bold md:text-4xl">
                  {product.title}
                </h1>
                <StockBadge status={stock} />
              </div>
              <p className="mt-4 text-2xl font-bold text-primary font-body">
                {variant ? formatPriceNok(variant.priceOre) : ""}
              </p>
              <CollapsibleDescription
                html={product.descriptionHtml}
                plain={product.description}
              />

              {product.variants.length > 1 && (
                <div className="mt-8">
                  <label className="mb-2 block text-sm font-medium font-body">
                    Variant
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {product.variants.map((v, i) => (
                      <button
                        key={v.id}
                        onClick={() => setSelectedVariantIdx(i)}
                        className={`rounded-full border px-4 py-2 text-sm font-body transition-all ${
                          i === selectedVariantIdx
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border hover:border-primary"
                        }`}
                      >
                        {v.title}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-8">
                <label
                  htmlFor="product-quantity"
                  className="mb-2 block text-xs uppercase tracking-[0.2em] text-primary font-semibold"
                >
                  Antall
                </label>
                <div
                  className="inline-flex items-center rounded-full border border-border bg-card"
                  role="group"
                  aria-label="Antall"
                >
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    disabled={quantity <= 1 || soldOut}
                    aria-label="Reduser antall"
                    className="flex h-10 w-10 items-center justify-center rounded-l-full text-muted-foreground transition-colors hover:bg-secondary disabled:opacity-40"
                  >
                    <Minus className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <input
                    id="product-quantity"
                    type="number"
                    min={1}
                    max={maxQty}
                    value={quantity}
                    onChange={(e) => {
                      const next = Number.parseInt(e.target.value, 10);
                      if (Number.isNaN(next)) return;
                      setQuantity(Math.max(1, Math.min(maxQty, next)));
                    }}
                    disabled={soldOut}
                    className="h-10 w-14 border-0 bg-transparent text-center text-sm font-body font-semibold outline-none focus:ring-0 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    aria-label="Antall"
                  />
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => Math.min(maxQty, q + 1))}
                    disabled={quantity >= maxQty || soldOut}
                    aria-label="Øk antall"
                    className="flex h-10 w-10 items-center justify-center rounded-r-full text-muted-foreground transition-colors hover:bg-secondary disabled:opacity-40"
                  >
                    <Plus className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              </div>

              <button
                ref={addToCartRef}
                onClick={handleAddToCart}
                disabled={!variant || soldOut}
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-primary px-8 py-4 font-body font-semibold text-primary-foreground shadow-lg transition-all hover:opacity-90 hover:-translate-y-0.5 disabled:translate-y-0 disabled:opacity-50"
              >
                <ShoppingBag className="h-5 w-5" />
                {soldOut ? "Utsolgt" : "Legg i handlekurv"}
              </button>

              <PaymentBadges />
              <ProductTrustPanel />
            </div>
          </AnimateOnScroll>
        </div>
      </div>
    </Layout>
  );
};

export default ProductDetail;
